import calendarImage from '../assets/calendar.png'
import galaxyBackground from '../assets/promptings-galaxy-background.avif'
import heroImage from '../assets/homepage-hero.jpg'
import lavenderMobileEditorImage from '../assets/lavender-mobile-editor.png'
import loveYouMomImage from '../assets/love-you-mom.png'
import printedDigitalImage from '../assets/printed-digital.png'
import scrumptiousAssortmentImage from '../assets/scrumptious-assortment.png'

export type LaunchAction =
  | { type: 'send_free_card' }
  | { type: 'pricing' }
  | { type: 'scroll_to'; targetId: string }
  | { type: 'external_url'; url: string }

export type LaunchActionLink = LaunchAction & {
  label: string
}

export type LaunchMedia =
  | {
      kind: 'image'
      src: string
      alt: string
      objectPosition?: string
      aspectRatio?: 'default' | 'portrait_2_3'
    }
  | {
      kind: 'card_collage'
      cards: ReadonlyArray<{
        src: string
        alt: string
        isHorizontal: boolean
      }>
    }
  | {
      kind: 'stack'
      src: string
      alt: string
      accentSrc: string
      accentAlt: string
      tertiarySrc?: string
      tertiaryAlt?: string
    }

export type LaunchPoint =
  | { label: string; kind: 'scroll'; targetId: string }
  | { label: string; kind: 'info'; modalKey: 'customization' | 'signatures' }
  | { label: string; kind: 'static' }

export type LaunchSection = {
  id: string
  sectionKey:
    | 'hero'
    | 'act-on-promptings'
    | 'digital-and-printed'
    | 'gifts'
    | 'customization'
    | 'crm'
    | 'automation'
  theme: 'dark_galaxy' | 'light' | 'soft_gray'
  layout: 'image_left' | 'image_right'
  eyebrow: string
  title: string
  accent?: string
  body: string
  supportingPoints?: ReadonlyArray<LaunchPoint>
  media: LaunchMedia
  primaryAction?: LaunchActionLink
  secondaryAction?: LaunchActionLink
}

export type LaunchCatalogCard = {
  previewUrl: string
  isHorizontal: boolean
}

export const launchSections = {
  hero: {
    id: 'hero',
    sectionKey: 'hero',
    theme: 'dark_galaxy',
    layout: 'image_right',
    eyebrow: 'The premier gratitude sharing platform',
    title:
      'Act on every prompting with gratitude that reaches people where they are.',
    accent: 'gratitude',
    body:
      'Promptings helps you turn a thoughtful moment into a card, gift, reminder, or follow-up system that feels personal, timely, and meaningful.',
    supportingPoints: [
      { label: 'Our Catalog', kind: 'scroll', targetId: 'cards-ribbon' },
      {
        label: 'Beautiful cards',
        kind: 'scroll',
        targetId: 'act-on-promptings',
      },
      {
        label: 'Digital and printed delivery',
        kind: 'scroll',
        targetId: 'digital-and-printed',
      },
      { label: 'CRM and automation', kind: 'scroll', targetId: 'crm' },
      { label: 'Customization', kind: 'scroll', targetId: 'customization' },
      { label: 'Gifts', kind: 'scroll', targetId: 'gifts' },
    ],
    media: {
      kind: 'image',
      src: heroImage,
      alt: 'Promptings greeting cards arranged in a hero collage',
      objectPosition: 'center center',
    },
    primaryAction: {
      label: 'Send a Free Card',
      type: 'send_free_card',
    },
  },
  actOnPromptings: {
    id: 'act-on-promptings',
    sectionKey: 'act-on-promptings',
    theme: 'light',
    layout: 'image_left',
    eyebrow: 'Start with the moment',
    title: 'Create a card the second someone comes to mind.',
    body:
      'Choose from beautifully designed catalog cards, add your own photos, or build something completely personal. Promptings makes it simple to turn appreciation into action before the moment passes.',
    supportingPoints: [
      { label: 'Catalog cards for every occasion', kind: 'static' },
      { label: 'Photo Drop designs that feel personal', kind: 'static' },
      {
        label: 'Build-your-own customization',
        kind: 'info',
        modalKey: 'customization',
      },
    ],
    media: {
      kind: 'image',
      src: loveYouMomImage,
      alt: 'Love you mom greeting card design',
      objectPosition: 'center center',
    },
    primaryAction: {
      label: 'Send a Free Card',
      type: 'send_free_card',
    },
    secondaryAction: {
      label: 'View Pricing',
      type: 'pricing',
    },
  },
  digitalAndPrinted: {
    id: 'digital-and-printed',
    sectionKey: 'digital-and-printed',
    theme: 'dark_galaxy',
    layout: 'image_right',
    eyebrow: 'Digital when it needs to be fast. Printed when it needs to last.',
    title: 'One prompting can become an instant message or a keepsake card.',
    body:
      'Send digitally for immediate impact, or print and mail a real card that becomes a lasting reminder of the moment you chose to act.',
    supportingPoints: [
      { label: 'Digital ACTs and messages', kind: 'static' },
      { label: 'Printed cards and keepsakes', kind: 'static' },
      { label: 'Fast sends and mailbox moments', kind: 'static' },
    ],
    media: {
      kind: 'image',
      src: printedDigitalImage,
      alt: 'Promptings printed and digital card delivery options on a phone',
      objectPosition: 'center center',
    },
    primaryAction: {
      label: 'Explore Options',
      type: 'pricing',
    },
  },
  gifts: {
    id: 'gifts',
    sectionKey: 'gifts',
    theme: 'soft_gray',
    layout: 'image_left',
    eyebrow: 'When the moment calls for more',
    title: 'Add a thoughtful gift that makes your message unforgettable.',
    body:
      'Pair your card with curated gifts, treats, and keepsakes that turn a simple prompting into a personal experience they can open, enjoy, and remember.',
    supportingPoints: [
      { label: 'Curated gift options', kind: 'static' },
      { label: 'Cards and gifts in one flow', kind: 'static' },
      { label: 'Thoughtful touches for deeper connection', kind: 'static' },
    ],
    media: {
      kind: 'image',
      src: scrumptiousAssortmentImage,
      alt: 'Assorted treats and gifts',
      objectPosition: 'center center',
    },
    primaryAction: {
      label: 'Explore Gifts',
      type: 'external_url',
      url: '/store',
    },
    secondaryAction: {
      label: 'Customize a Card',
      type: 'scroll_to',
      targetId: 'customization',
    },
  },
  customization: {
    id: 'customization',
    sectionKey: 'customization',
    theme: 'light',
    layout: 'image_right',
    eyebrow: 'Make it unmistakably yours',
    title: 'Customize every detail without slowing down the moment.',
    body:
      'Personalize the front, inside, and back of your card with photos, handwriting, signatures, colors, and layouts that match the message you want to send.',
    supportingPoints: [
      {
        label: 'Personal handwriting and signature',
        kind: 'info',
        modalKey: 'signatures',
      },
      {
        label: 'Photo and layout customization',
        kind: 'info',
        modalKey: 'customization',
      },
      { label: 'Branded options for businesses', kind: 'static' },
    ],
    media: {
      kind: 'image',
      src: lavenderMobileEditorImage,
      alt: 'Promptings mobile card editor with lavender design tools',
      objectPosition: 'center center',
      aspectRatio: 'portrait_2_3',
    },
    secondaryAction: {
      label: 'View Pricing',
      type: 'pricing',
    },
  },
  crm: {
    id: 'crm',
    sectionKey: 'crm',
    theme: 'soft_gray',
    layout: 'image_left',
    eyebrow: 'Wider reach',
    title: 'A card-inclusive CRM for staying top of mind automatically.',
    accent: 'card-inclusive CRM',
    body:
      'Promptings helps you organize relationships, remember important dates, and create thoughtful follow-up systems that keep every touchpoint warm, intentional, and human.',
    supportingPoints: [
      { label: 'Contact groups and relationship details', kind: 'static' },
      { label: 'Birthday and anniversary reminders', kind: 'static' },
      { label: 'Campaigns for consistent follow-up', kind: 'static' },
      { label: 'Automated reminders and recurring campaigns', kind: 'static' },
      { label: 'System Expert help when you want support', kind: 'static' },
    ],
    media: {
      kind: 'image',
      src: calendarImage,
      alt: 'Promptings relationship calendar on iPhone',
      objectPosition: 'center center',
    },
    secondaryAction: {
      label: 'View Packages',
      type: 'pricing',
    },
  },
  automation: {
    id: 'automation',
    sectionKey: 'automation',
    theme: 'dark_galaxy',
    layout: 'image_right',
    eyebrow: 'Stay in touch without starting from scratch',
    title: 'Build repeatable gratitude systems that still feel personal.',
    body:
      'From reminders to multi-touch campaigns, Promptings helps you send the right message at the right time while keeping every touchpoint warm, intentional, and human.',
    supportingPoints: [
      { label: 'Automated reminders', kind: 'static' },
      { label: 'Recurring card campaigns', kind: 'static' },
      { label: 'Business and personal relationship systems', kind: 'static' },
    ],
    media: {
      kind: 'image',
      src: calendarImage,
      alt: 'Promptings calendar and relationship automation experience',
      objectPosition: 'center center',
    },
    primaryAction: {
      label: 'View Packages',
      type: 'pricing',
    },
    secondaryAction: {
      label: 'Back to top',
      type: 'scroll_to',
      targetId: 'hero',
    },
  },
} as const

export const launchCatalogCards: LaunchCatalogCard[] = [
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/l1Ir8Ss8WY0XMxEa6pEhSQeLMig=/fit-in/700x0/system_images/2019/12/4/89bb0de8-a6f3-4577-8498-1e5d611b66b8.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/manual/soc-web-assets-kt.212.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/0MNyTUIJ59tbLBw2n9ubQZKcM7s=/fit-in/700x0/system_images/2023/4/5/5c83f228-2278-4325-84c6-9999a9220704.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/mXeM5PvgvIUfJ_jWCNo0_cQzPAM=/fit-in/700x0/system_images/2022/11/18/122f2557-5498-4f88-84cd-c47024a257b8.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/Ull0NhiMOHHuQ3fe8K7OlNlR2PQ=/fit-in/320x0/system_images/2021/10/5/e3a7189e-454c-4930-8d7e-6b2900a72cc9.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/em2a3lTin6EyFlIzq2aCPTlHTiQ=/fit-in/320x0/system_images/2025/5/6/72c53ee3-f923-48e4-8ff1-d3a45fb7fef9.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/Sd4KK5Qy_K8ms572AqSysfbhxtQ=/fit-in/700x0/system_images/2022/2/18/c7105cf9-fd46-46cb-a1aa-e2895150ce1a.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/L4ACmmJ1pEV3zJ-C0AhS4P9scsE=/fit-in/320x0/system_images/2020/5/18/d7bf2927-8684-4203-bd6b-ff9d3947d7d9.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/plELQYxjJDKX13xIn01icv1iE4k=/fit-in/700x0/system_images/2025/6/11/cf0c252a-5a0a-41bd-b970-d0a3158f162a.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/UDHUMqJ8FYkonW2Q57TXLQXq_ig=/fit-in/700x0/system_images/2020/8/12/72a5494b-eb28-4315-a839-9122af89a465.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/0F2c_sYzVLRz9gYGid4o5W1tBtA=/fit-in/700x0/system_images/2019/12/4/0fab54d8-4b71-4923-89da-a257e7c182ce.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/-wrSVB3KmUyY4CtKeuq3kYaB83Q=/fit-in/700x0/system_images/2024/1/18/d88aab2b-6d3f-408f-994a-7363960013b4.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/LEbWCaCMaivyIbSNCpKSFNuOdRg=/fit-in/700x0/system_images/2020/11/13/fa24ba10-b7a7-4f9c-9dfd-6b7c73ff990d.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/KYdvdBAa1Z-wR4f_FS7s6NWbsl4=/fit-in/700x0/system_images/2025/3/19/f9b09346-f216-4ff0-abc5-0046e3a3f85a.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/XOViRNCLp5QuetOR3i85si8IeT8=/fit-in/700x0/system_images/2022/2/10/702ca5b5-3f2e-4d6c-98b7-823756ee1953.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/OHYj0FZYlqLwTRBvG4RKiRZeWaU=/fit-in/700x0/system_images/2025/6/6/7eb332a4-8091-410b-b954-ef66e84a9539.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/j8viXpj9OJn70a3ShlHNyvFWc8k=/fit-in/700x0/system_images/2023/2/2/196dc2f0-2ab1-402b-abb6-390c21b391d8.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/rmqaJl9AzS56r0hz9P5y_4RuflQ=/fit-in/320x0/system_images/2020/9/27/4f9e8143-a103-4b04-83bd-f0d52636c2c5.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/HyD47E4-dScrC811zqVUkbqDx8c=/fit-in/700x0/high_res_catalog_image/2017/9/11/0/appreciated__bluefront.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/k1sRnK1kSCpSrtTtEhNMjrPhfkU=/fit-in/700x0/system_images/2025/5/6/d32f3cc5-f42e-43bb-b3f8-df704ad3e167.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/wL5rnvA2DnoSyGuAVwtZBd1gXEg=/fit-in/700x0/system_images/2021/12/2/8e74901a-9db2-4105-9dae-2d37ed78261b.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/ibLMb4IHqGZLwrMxLN1BFfjlA1c=/fit-in/700x0/system_images/2025/11/19/c01bf99c-387f-40ce-a6ec-f8ae29f413ca.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/FZfA3J1NzlDWtvPSTA3r6rIbmg0=/fit-in/700x0/high_res_catalog_image/2009/10/5/11/11699872_.png',
    isHorizontal: true,
  },

  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/_KOxw4_Nnlrb6CFrwaXb9-dHwp8=/fit-in/320x0/system_images/2020/3/6/58fcd340-b8bd-4a58-9484-c91d44875d27.png',
    isHorizontal: false,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/tvmHxP8s0SecHVBx6bPcpiciLAY=/fit-in/700x0/system_images/2020/7/15/6b0fe864-ea88-48da-8a3c-d533ff095b1d.png',
    isHorizontal: true,
  },
  {
    previewUrl:
      'https://www.sendoutcards.com/thumbnail/TM2HP8P2eoICJx4ql7mmoV1PyaY=/fit-in/700x0/system_images/2019/12/4/bcfeb13e-bbe1-41d6-9733-a3a4fb3adc70.png',
    isHorizontal: false,
  },
]

export { galaxyBackground }
